<script>
import MessagePreview from 'dashboard/components/widgets/conversation/MessagePreview.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  IG_REEL: 'ig_reel',
  AUDIO: 'audio',
};

export default {
  name: 'BubbleForward',
  components: {
    MessagePreview,
    GalleryView,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    messageType: {
      type: Number,
      required: true,
    },
    parentHasAttachments: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      MESSAGE_TYPE,
      showGalleryViewer: false,
      attachment: {},
      availableAttachments: [],
    };
  },
  computed: {
    isForwarded() {
      return this.message.content_attributes?.to_forward === true;
    },
  },
  methods: {
    scrollToMessage() {
      // Emite o evento de scroll
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
        messageId: this.message.id,
      });

      // Verifica se há anexos
      if (this.message.attachments && this.message.attachments.length > 0) {
        const attachment = this.message.attachments[0];

        // Usa Object.values para verificar se file_type está em ALLOWED_FILE_TYPES
        if (Object.values(ALLOWED_FILE_TYPES).includes(attachment.file_type) && attachment.data_url) {
          // Abre o GalleryView para tipos de anexo suportados
          this.openImagePreview(attachment.data_url, attachment.thumb_url, this.message.id);
        } else if (attachment.data_url) {
          // Abre outros tipos de anexos em uma nova aba
          window.open(attachment.data_url, '_blank');
        }
      }
    },
    openImagePreview(dataUrl, thumbnailUrl, messageId) {
      this.showGalleryViewer = true;
      this.attachment = {
        file_type: this.message.attachments[0].file_type,
        data_url: dataUrl,
        thumb_url: thumbnailUrl || dataUrl,
        message_id: messageId,
      };
      // Inclui todos os anexos suportados
      this.availableAttachments = this.message.attachments
        .filter(att => Object.values(ALLOWED_FILE_TYPES).includes(att.file_type) && att.data_url)
        .map(att => ({
          file_type: att.file_type,
          data_url: att.data_url,
          thumb_url: att.thumb_url || att.data_url,
          message_id: att.message_id || Math.floor(Math.random() * 100),
        }));
    },
    onClose() {
      this.showGalleryViewer = false;
      this.resetAttachmentData();
    },
    resetAttachmentData() {
      this.attachment = {};
      this.availableAttachments = [];
    },
  },
};
</script>

<template>
  <div
    v-if="isForwarded"
    class="px-2 py-1.5 rounded-sm min-w-[10rem] mb-2"
    :class="{
      'bg-slate-50 dark:bg-slate-600 dark:text-slate-50': messageType === MESSAGE_TYPE.INCOMING,
      'bg-woot-600 text-woot-50': messageType === MESSAGE_TYPE.OUTGOING,
      '-mx-2': !parentHasAttachments,
    }"
    @click="scrollToMessage"
  >
    <div class="flex items-center mb-1 text-xs text-slate-500 dark:text-slate-300">
  <span aria-hidden="true" data-icon="forward-refreshed" class="mr-1">
    <svg viewBox="0 0 24 24" height="16" width="16" preserveAspectRatio="xMidYMid meet" fill="none">
      <path d="M19.175 11L15.3 7.12505C15.1 6.92505 15 6.68755 15 6.41255C15 6.13755 15.1 5.90005 15.3 5.70005C15.5 5.51672 15.7375 5.42505 16.0125 5.42505C16.2875 5.42505 16.5167 5.51672 16.7 5.70005L21.3 10.3C21.4 10.4 21.4708 10.5084 21.5125 10.625C21.5542 10.7417 21.575 10.8667 21.575 11C21.575 11.1334 21.5542 11.2584 21.5125 11.375C21.4708 11.4917 21.4 11.6 21.3 11.7L16.7 16.3C16.5 16.5 16.2667 16.5959 16 16.5875C15.7333 16.5792 15.5 16.4834 15.3 16.3C15.1 16.1 14.9958 15.8667 14.9875 15.6C14.9792 15.3334 15.075 15.1 15.275 14.9L19.175 11ZM13.175 12H7C6.16667 12 5.45833 12.2917 4.875 12.875C4.29167 13.4584 4 14.1667 4 15V18C4 18.2834 3.90417 18.5209 3.7125 18.7125C3.52083 18.9042 3.28333 19 3 19C2.71667 19 2.47917 18.9042 2.2875 18.7125C2.09583 18.5209 2 18.2834 2 18V15C2 13.6167 2.4875 12.4375 3.4625 11.4625C4.4375 10.4875 5.61667 10 7 10H13.175L10.3 7.12505C10.1 6.92505 10 6.68755 10 6.41255C10 6.13755 10.1 5.90005 10.3 5.70005C10.5 5.51672 10.7375 5.42505 11.0125 5.42505C11.2875 5.42505 11.5167 5.51672 11.7 5.70005L16.3 10.3C16.4 10.4 16.4708 10.5084 16.5125 10.625C16.5542 10.7417 16.575 10.8667 16.575 11C16.575 11.1334 16.5542 11.2584 16.5125 11.375C16.4708 11.4917 16.4 11.6 16.3 11.7L11.7 16.3C11.5 16.5 11.2667 16.5959 11 16.5875C10.7333 16.5792 10.5 16.4834 10.3 16.3C10.1 16.1 9.99583 15.8667 9.9875 15.6C9.97917 15.3334 10.075 15.1 10.275 14.9L13.175 12Z" fill="currentColor"></path>
    </svg>
  </span>
      <span>{{ $t('CONVERSATION.FORWARDED_MESSAGE') }}</span>
    </div>

    <GalleryView
      v-if="showGalleryViewer"
      :show="showGalleryViewer"
      :attachment="attachment"
      :all-attachments="availableAttachments"
      @error="onClose"
      @close="onClose"
      @update:show="showGalleryViewer = $event"
    />
  </div>
</template>

<style scoped>


.text-xs {
  font-style: italic;
}
</style>
